import { ChapletType } from '../types';

const CHAPLET_FAVORITES_KEY = 'ora-chaplet-favorites';

export interface ChapletFavoritesData {
  favorites: ChapletType[];
  lastUpdated: string;
}

// Default favorites for new users
const DEFAULT_FAVORITES: ChapletType[] = ['divine-mercy', 'st-michael', 'sacred-heart', 'seven-sorrows'];

/**
 * Load chaplet favorites from localStorage
 */
export const loadChapletFavorites = (): ChapletType[] => {
  try {
    const stored = localStorage.getItem(CHAPLET_FAVORITES_KEY);
    if (!stored) {
      return DEFAULT_FAVORITES;
    }

    const data: ChapletFavoritesData = JSON.parse(stored);

    // Validate that stored favorites are valid chaplet types
    if (Array.isArray(data.favorites)) {
      return data.favorites.filter(favorite =>
        typeof favorite === 'string' && favorite.length > 0
      );
    }

    return DEFAULT_FAVORITES;
  } catch (error) {
    console.warn('Failed to load chaplet favorites:', error);
    return DEFAULT_FAVORITES;
  }
};

/**
 * Save chaplet favorites to localStorage
 */
export const saveChapletFavorites = (favorites: ChapletType[]): void => {
  try {
    const data: ChapletFavoritesData = {
      favorites,
      lastUpdated: new Date().toISOString()
    };

    localStorage.setItem(CHAPLET_FAVORITES_KEY, JSON.stringify(data));

    // Dispatch custom event for same-tab synchronization
    window.dispatchEvent(new CustomEvent('chaplet-favorites-changed', {
      detail: { favorites }
    }));
  } catch (error) {
    console.error('Failed to save chaplet favorites:', error);
  }
};

/**
 * Add a chaplet to favorites
 */
export const addChapletFavorite = (chapletType: ChapletType): ChapletType[] => {
  const currentFavorites = loadChapletFavorites();

  if (!currentFavorites.includes(chapletType)) {
    const newFavorites = [...currentFavorites, chapletType];
    saveChapletFavorites(newFavorites);
    return newFavorites;
  }

  return currentFavorites;
};

/**
 * Remove a chaplet from favorites
 */
export const removeChapletFavorite = (chapletType: ChapletType): ChapletType[] => {
  const currentFavorites = loadChapletFavorites();
  const newFavorites = currentFavorites.filter(favorite => favorite !== chapletType);

  saveChapletFavorites(newFavorites);
  return newFavorites;
};

/**
 * Toggle chaplet favorite status
 */
export const toggleChapletFavorite = (chapletType: ChapletType): {
  favorites: ChapletType[],
  isFavorite: boolean
} => {
  const currentFavorites = loadChapletFavorites();
  const isFavorite = currentFavorites.includes(chapletType);

  let newFavorites: ChapletType[];

  if (isFavorite) {
    newFavorites = removeChapletFavorite(chapletType);
  } else {
    newFavorites = addChapletFavorite(chapletType);
  }

  return {
    favorites: newFavorites,
    isFavorite: !isFavorite
  };
};

/**
 * Check if a chaplet is favorited
 */
export const isChapletFavorite = (chapletType: ChapletType): boolean => {
  const favorites = loadChapletFavorites();
  return favorites.includes(chapletType);
};

/**
 * Reorder favorites
 */
export const reorderChapletFavorites = (reorderedFavorites: ChapletType[]): void => {
  saveChapletFavorites(reorderedFavorites);
};

/**
 * Get favorites with fallback to defaults if empty
 */
export const getFavoriteChapletsForDisplay = (): ChapletType[] => {
  const favorites = loadChapletFavorites();

  // Ensure we always show at least 4 chaplets for the prayer selection screen
  if (favorites.length < 4) {
    const allFavorites = [...favorites];

    // Add default chaplets if needed to reach 4
    for (const defaultChaplet of DEFAULT_FAVORITES) {
      if (!allFavorites.includes(defaultChaplet) && allFavorites.length < 4) {
        allFavorites.push(defaultChaplet);
      }
    }

    return allFavorites.slice(0, 4);
  }

  return favorites.slice(0, 4); // Show first 4 favorites
};